import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './utils/authUtils';
import { nacmeLogo } from './assets/images';

const BACKEND_URL = API_URL;

const BoardLiaisonDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liaisonInfo, setLiaisonInfo] = useState(null);
  const [companyRecruiters, setCompanyRecruiters] = useState([]);
  const [recruitersData, setRecruitersData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('boardLiaisonToken');
    const storedLiaisonInfo = localStorage.getItem('boardLiaisonInfo');
    
    if (!token || !storedLiaisonInfo) {
      navigate('/board-liaison/login');
      return;
    }

    try {
      setLiaisonInfo(JSON.parse(storedLiaisonInfo));
      fetchCompanyRecruiters(token);
    } catch (err) {
      console.error('Error parsing liaison info:', err);
      navigate('/board-liaison/login');
    }
  }, [navigate]);

  const fetchCompanyRecruiters = async (token) => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/board-liaison/company-recruiters`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setRecruitersData(response.data);
      setCompanyRecruiters(response.data.recruiters || []);
    } catch (err) {
      console.error('Error fetching recruiters:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('boardLiaisonToken');
        localStorage.removeItem('boardLiaisonInfo');
        navigate('/board-liaison/login');
      } else {
        setError(err.response?.data?.detail || 'Failed to load company recruiters');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewRecruiterDashboard = (recruiterId) => {
    // Open the recruiter dashboard in board liaison view mode
    // This reuses the existing RecruiterDashboard component with special parameters
    window.open(`/recruiter/dashboard?boardLiaisonView=true&recruiterId=${recruiterId}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('boardLiaisonToken');
    localStorage.removeItem('boardLiaisonInfo');
    navigate('/');
  };

  const getStatusBadge = (recruiter) => {
    if (!recruiter.approved) {
      return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">Pending Approval</span>;
    }
    if (!recruiter.is_active) {
      return <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">Inactive</span>;
    }
    return <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Active</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading company recruiters...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <img
                className="h-10 w-auto mr-4"
                src={nacmeLogo}
                alt="NACME Logo"
              />
              {recruitersData?.company_logo && (
                <img
                  className="h-8 w-auto mr-4"
                  src={recruitersData.company_logo.startsWith('/public/') 
                    ? recruitersData.company_logo.replace('/public/', '/') 
                    : recruitersData.company_logo}
                  alt={`${recruitersData.company_name} Logo`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Board Liaison Portal</h1>
                <p className="text-sm text-gray-600">{recruitersData?.company_name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{liaisonInfo?.full_name}</p>
                <p className="text-xs text-gray-500">{liaisonInfo?.title}</p>
                {liaisonInfo?.can_write && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Write Access
                  </span>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-semibold">👥</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Recruiters
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {recruitersData?.total_recruiters || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-semibold">✓</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Active Recruiters
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {companyRecruiters.filter(r => r.approved && r.is_active).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-semibold">⏳</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Pending Approval
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {companyRecruiters.filter(r => !r.approved).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recruiters Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Company Recruiters
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Overview of all recruiters associated with {recruitersData?.company_name}
              </p>
            </div>
            <div className="border-t border-gray-200">
              {companyRecruiters.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No recruiters found for your company.</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {companyRecruiters.map((recruiter) => (
                    <li key={recruiter.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {recruiter.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">
                                {recruiter.name}
                              </p>
                              <div className="ml-2">
                                {getStatusBadge(recruiter)}
                              </div>
                            </div>
                            <p className="text-sm text-gray-500">{recruiter.email}</p>
                            {recruiter.title && (
                              <p className="text-sm text-gray-500">{recruiter.title}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right text-sm text-gray-500">
                            <p>Joined: {recruiter.created_at ? new Date(recruiter.created_at).toLocaleDateString() : '---'}</p>
                            <p>Last active: {recruiter.last_activity ? new Date(recruiter.last_activity).toLocaleDateString() : '---'}</p>
                          </div>
                          {recruiter.approved && recruiter.is_active && (
                            <button
                              onClick={() => handleViewRecruiterDashboard(recruiter.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              View Dashboard
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BoardLiaisonDashboard;